import { computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthenticationContextStore } from '@core/services/authentication-context.store';
import { TokenStore } from '@core/services/token.store';
import { environment } from '@env';
import { ImpersonationSessionStore } from '@features/authentication/stores/impersonation-session.store';
import type { DeviceCookieClaims } from '@features/authentication/types/authentication.types';
import { AuthSourceLabelMap, DeviceIdScopeLabelMap } from '@features/authentication/types/authentication.types';
import { AuthorizationStore } from '@features/authorization/stores/authorization.store';
import { AzureLoginService } from '@features/login/services/azure-login.service';
import type { DeviceIdScope } from '@kerberos-compliance/compliance-shared-ts/core/iam/authentication/enums/device-id-scope.enum';
import { DeviceIdScopeMap } from '@kerberos-compliance/compliance-shared-ts/core/iam/authentication/enums/device-id-scope.enum';
import type { DxStatusContributor } from '@kerberos-compliance/compliance-shared-ui/dx';
import { DxStatusVariantMap } from '@kerberos-compliance/compliance-shared-ui/dx';

const DeviceIdScopes: ReadonlySet<unknown> = new Set(Object.values(DeviceIdScopeMap));

/**
 * Whether a raw `dids` claim value is one of the known desk scopes.
 *
 * @param {unknown} value - The raw claim value.
 * @returns {boolean} True for a known {@link DeviceIdScope}.
 */
function isDeviceIdScope(value: unknown): value is DeviceIdScope {
  return DeviceIdScopes.has(value);
}

/**
 * Renders the build's debug flags, saying so when there are none rather than showing an
 * empty cell that reads as "not collected".
 *
 * @param {string[]} [debugFlags] - The flags this build was compiled with.
 * @returns {string} The flags, comma-separated, or `None`.
 */
function dxDebugFlagsLabel(debugFlags: readonly string[] | undefined): string {
  return debugFlags === undefined || debugFlags.length === 0 ? 'None' : debugFlags.join(', ');
}

/**
 * Build and deployment facts.
 *
 * Static for the life of the tab, but contributed rather than hardcoded in the shared
 * panel: the environment shape is an app type, and the endpoints below are the first
 * thing anyone checks when "it works locally".
 *
 * @returns {DxStatusContributor} The build section for the shared status panel.
 */
export function dxBuildStatusContributor(): DxStatusContributor {
  const router = inject(Router);
  const isSentryConfigured = Boolean(environment.sentryUrl);

  return {
    id: 'build',
    label: 'Build',
    order: 1,
    sections: computed(() => [
      {
        id: 'environment',
        label: 'Environment',
        rows: [
          { key: 'Target', value: environment.target },
          { key: 'Version', value: environment.version },
          { key: 'Build type', value: environment.productionBuild ? 'Production' : 'Development' },
          { key: 'Console logging', value: environment.enableConsoleLogging ? 'On' : 'Off' },
          { key: 'MSAL', value: environment.enableMsal ? 'Enabled' : 'Disabled' },
          { key: 'Debug flags', value: dxDebugFlagsLabel(environment.debugFlags) },
          {
            key: 'Sentry',
            value: isSentryConfigured ? 'Configured' : 'Disabled / local',
            variant: isSentryConfigured ? DxStatusVariantMap.Default : DxStatusVariantMap.Warning,
          },
          { key: 'Route', value: router.url, isCode: true },
        ],
      },
      {
        id: 'endpoints',
        label: 'Endpoints',
        rows: [
          { key: 'AML API', value: environment.amlBaseApiUrl, isCode: true },
          { key: 'AML Desk', value: environment.amlDeskPlatformUrl ?? 'Not configured', isCode: true },
          { key: 'Maintenance info', value: environment.maintenanceInfoUrl ?? 'Not configured', isCode: true },
        ],
      },
    ]),
  };
}

/**
 * Who this session is, and what it is allowed to do.
 *
 * The identifiers here — display name, address, subject — are what the session already
 * holds and already shows in the toolbar, so nothing is exposed that the operator is not
 * already looking at. No token material is contributed: `TokenStore` is read for validity
 * only, never for the token itself.
 *
 * @returns {DxStatusContributor} The session section for the shared status panel.
 */
export function dxSessionStatusContributor(): DxStatusContributor {
  const tokenStore = inject(TokenStore);
  const authContextStore = inject(AuthenticationContextStore);
  const impersonationStore = inject(ImpersonationSessionStore);
  const authorizationStore = inject(AuthorizationStore);
  const azureLoginService = inject(AzureLoginService);

  return {
    id: 'session',
    label: 'Session',
    order: 2,
    sections: computed(() => {
      const user = authContextStore.getAuthenticatedUser();
      const { dids, asrc } = (user ?? {}) as DeviceCookieClaims;
      const deviceIdScope = isDeviceIdScope(dids) ? dids : undefined;
      const grants = authorizationStore.userGrants();
      const isImpersonating = impersonationStore.hasActiveSession();

      return [
        {
          id: 'authentication',
          label: 'Authentication',
          rows: [
            {
              key: 'Authenticated',
              value: tokenStore.isAuthenticated() ? 'Yes' : 'No',
              variant: tokenStore.isAuthenticated() ? DxStatusVariantMap.Success : DxStatusVariantMap.Warning,
            },
            { key: 'Valid token', value: tokenStore.hasValidToken() ? 'Yes' : 'No' },
            { key: 'Azure account', value: azureLoginService.hasActiveAccount() ? 'Active' : 'None' },
            { key: 'Azure signed in', value: azureLoginService.isLoggedIn() ? 'Yes' : 'No' },
            { key: 'User', value: user?.name ?? user?.mail ?? 'Anonymous' },
            {
              key: 'Impersonating',
              value: isImpersonating ? 'Active' : 'None',
              variant: isImpersonating ? DxStatusVariantMap.Danger : DxStatusVariantMap.Default,
            },
            // The two claims that pick the device cookie. The cookie itself is HttpOnly and never visible here;
            // a missing scope means a token from before the claim existed, which the next refresh rejects.
            {
              key: 'Desk (dids)',
              value: deviceIdScope ? `${DeviceIdScopeLabelMap[deviceIdScope]} (${deviceIdScope})` : 'None',
              variant: deviceIdScope ? DxStatusVariantMap.Default : DxStatusVariantMap.Warning,
              isCode: true,
            },
            {
              key: 'Provider (asrc)',
              value: asrc === undefined ? 'None' : `${AuthSourceLabelMap[asrc] ?? 'Unknown'} (${asrc})`,
              isCode: true,
            },
          ],
          data: {
            isAuthenticated: tokenStore.isAuthenticated(),
            deviceIdScope: dids,
            authSource: asrc,
            hasValidToken: Boolean(tokenStore.hasValidToken()),
            hasActiveAccount: azureLoginService.hasActiveAccount(),
            isAzureLoggedIn: azureLoginService.isLoggedIn(),
            user: user ? { name: user.name, email: user.mail, sub: user.sub } : undefined,
            // Presence only. The session payload carries the return token that ends the
            // impersonation, and a snapshot gets pasted into tickets.
            impersonation: { isImpersonating, hasSession: impersonationStore.session() !== undefined },
          },
        },
        {
          id: 'grants',
          label: 'Grants',
          rows: [
            { key: 'Loaded', value: authorizationStore.grantsLoaded() ? 'Yes' : 'No' },
            { key: 'Count', value: String(grants.length) },
            { key: 'Roles', value: grants.map((grant) => grant.roleDisplayName).join(', ') || 'None' },
          ],
          data: {
            grantsLoaded: authorizationStore.grantsLoaded(),
            grantsCount: grants.length,
            roles: grants.map((grant) => grant.roleDisplayName),
          },
        },
      ];
    }),
  };
}
